import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { DoorOpen, Users, Baby, UserX2, RefreshCw, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const ROOM_ACCENTS = [
  { bg: "from-sky-100 to-blue-50", border: "border-sky-300", text: "text-sky-700", ring: "ring-sky-200" },
  { bg: "from-emerald-100 to-teal-50", border: "border-emerald-300", text: "text-emerald-700", ring: "ring-emerald-200" },
  { bg: "from-amber-100 to-yellow-50", border: "border-amber-300", text: "text-amber-700", ring: "ring-amber-200" },
  { bg: "from-fuchsia-100 to-pink-50", border: "border-fuchsia-300", text: "text-fuchsia-700", ring: "ring-fuchsia-200" },
  { bg: "from-indigo-100 to-violet-50", border: "border-indigo-300", text: "text-indigo-700", ring: "ring-indigo-200" },
  { bg: "from-rose-100 to-red-50", border: "border-rose-300", text: "text-rose-700", ring: "ring-rose-200" },
];

function RoomCard({ room, accent, onDrop, onDragStart, dragging, allowDrop, onCapacityChange }) {
  const empty = room.teacher_count === 0;
  const [over, setOver] = useState(false);
  return (
    <Card
      className={`overflow-hidden border-2 ${room.over_capacity ? "border-rose-400 ring-2 ring-rose-200" : accent.border} ${empty ? "opacity-70" : ""} ${over && allowDrop ? "ring-4 " + accent.ring + " scale-[1.02]" : ""} transition-all`}
      data-testid={`room-card-${room.room_number}`}
      onDragOver={(e) => { if (allowDrop) { e.preventDefault(); setOver(true); } }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); onDrop?.(room.room_number); }}
    >
      <div className={`h-2 bg-gradient-to-l ${accent.bg}`} />
      <CardContent className={`p-5 bg-gradient-to-br ${accent.bg}`}>
        <div className="flex items-center justify-between mb-3">
          <div className={`inline-flex items-center gap-2 font-black text-lg ${accent.text}`}>
            <DoorOpen className="w-6 h-6" /> قاعة {room.room_number}
          </div>
          <div className="flex gap-2">
            <div className="text-center px-3 py-1.5 rounded-xl bg-white/70 border border-white/80" title="عدد المعلمين">
              <div className="text-[10px] font-bold text-slate-500">معلمون</div>
              <div className={`text-lg font-black ${accent.text}`} data-testid={`room-${room.room_number}-teacher-count`}>{room.teacher_count}</div>
            </div>
            <div className="text-center px-3 py-1.5 rounded-xl bg-white/70 border border-white/80" title="عدد الأطفال المسندين">
              <div className="text-[10px] font-bold text-slate-500">أطفال</div>
              <div className={`text-lg font-black ${accent.text}`} data-testid={`room-${room.room_number}-child-count`}>{room.children_count}</div>
            </div>
          </div>
        </div>

        {empty ? (
          <div className={`text-center py-6 text-slate-500 text-sm rounded-xl border-2 border-dashed border-white ${over && allowDrop ? "bg-white/70" : "bg-white/40"}`}>
            <UserX2 className="w-6 h-6 mx-auto mb-1 opacity-60" />
            {over && allowDrop ? "أفلت هنا لنقل المعلم" : "لا يوجد معلمون في هذه القاعة"}
          </div>
        ) : (
          <div className="space-y-2">
            {room.teachers.map((t) => (
              <div
                key={t.id}
                className={`flex items-center gap-2.5 bg-white/70 border border-white/80 rounded-xl p-2 cursor-move hover:shadow-md transition-shadow ${dragging === t.id ? "opacity-40" : ""}`}
                draggable
                onDragStart={(e) => { onDragStart?.(t.id, room.room_number); e.dataTransfer.effectAllowed = "move"; }}
                data-testid={`room-${room.room_number}-teacher-${t.id}`}
              >
                <div className="w-9 h-9 rounded-xl overflow-hidden bg-gradient-to-br from-sky-500 to-blue-600 text-white flex items-center justify-center font-black text-xs shrink-0">
                  {t.photo_url ? <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover" /> : (t.name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm text-slate-800 truncate">{t.name}</div>
                  {t.specialty && <div className="text-[10px] text-slate-500 truncate">{t.specialty}</div>}
                </div>
                <div className="inline-flex items-center gap-1 text-[11px] font-black text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                  <Baby className="w-3 h-3" /> {t.assigned_children_count}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Capacity */}
        <div className={`mt-3 pt-3 border-t ${room.over_capacity ? "border-rose-200" : "border-white/50"} flex items-center justify-between gap-2`}>
          <div className="flex items-center gap-1 text-[11px]">
            <span className="font-bold text-slate-600">السعة:</span>
            <input
              type="number"
              min="0"
              max="200"
              defaultValue={room.capacity || 0}
              onBlur={(e) => onCapacityChange?.(room.room_number, parseInt(e.target.value || "0", 10))}
              className="w-14 h-7 px-2 rounded-md border border-white bg-white/80 text-center font-black text-slate-800"
              data-testid={`room-${room.room_number}-capacity-input`}
            />
          </div>
          {room.over_capacity && (
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-black animate-pulse" data-testid={`room-${room.room_number}-over-capacity`}>
              ⚠ تجاوز السعة ({room.children_count}/{room.capacity})
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function RoomsPage() {
  const [data, setData] = useState({ rooms: [], unassigned: null });
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(null); // teacher id currently being dragged
  const [dragFromRoom, setDragFromRoom] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/rooms-occupancy");
      setData(r.data);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (teacherId, fromRoom) => {
    setDragging(teacherId);
    setDragFromRoom(fromRoom);
  };
  const onDrop = async (toRoom) => {
    const tid = dragging;
    const from = dragFromRoom;
    setDragging(null);
    setDragFromRoom(null);
    if (!tid || from === toRoom) return;
    try {
      await api.patch(`/users/${tid}`, { room_number: toRoom });
      toast.success(`تم النقل إلى قاعة ${toRoom}`);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const onDropUnassigned = async () => {
    const tid = dragging;
    const from = dragFromRoom;
    setDragging(null);
    setDragFromRoom(null);
    if (!tid || from === 0) return;
    try {
      await api.patch(`/users/${tid}`, { room_number: 0 });
      toast.success("تم إزالة القاعة");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const onCapacityChange = async (roomNum, cap) => {
    try {
      const capacities = {};
      data.rooms.forEach((r) => { capacities[String(r.room_number)] = r.capacity || 0; });
      capacities[String(roomNum)] = Math.max(0, Math.min(200, cap || 0));
      await api.patch("/admin/room-capacities", { capacities });
      toast.success(`تم حفظ سعة قاعة ${roomNum}`);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const totalTeachers = data.rooms.reduce((s, r) => s + r.teacher_count, 0) + (data.unassigned?.teacher_count || 0);
  const totalChildren = data.rooms.reduce((s, r) => s + r.children_count, 0);

  return (
    <div className="p-6 space-y-6" data-testid="rooms-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-black text-slate-900 flex items-center gap-2">
            <DoorOpen className="w-8 h-8 text-indigo-600" /> خريطة القاعات
          </h1>
          <p className="text-sm text-slate-500 mt-1">نظرة سريعة على توزيع المعلمين والأطفال على قاعات المركز (1–6).</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-center px-4 py-2 rounded-2xl bg-indigo-50 border border-indigo-200">
            <div className="text-[10px] font-black text-indigo-700">إجمالي المعلمين</div>
            <div className="text-2xl font-black text-indigo-900" data-testid="total-teachers">{totalTeachers}</div>
          </div>
          <div className="text-center px-4 py-2 rounded-2xl bg-emerald-50 border border-emerald-200">
            <div className="text-[10px] font-black text-emerald-700">إجمالي الأطفال المسندين</div>
            <div className="text-2xl font-black text-emerald-900" data-testid="total-children">{totalChildren}</div>
          </div>
          <Button onClick={load} variant="outline" className="gap-2" data-testid="refresh-rooms-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> تحديث
          </Button>
        </div>
      </div>

      <div className="rounded-2xl bg-indigo-50 border-2 border-indigo-200 p-3 text-xs text-indigo-800 font-bold flex items-center gap-2" data-testid="drag-hint">
        <span className="text-lg">🖱️</span> يمكنك سحب أي بطاقة معلم وإفلاتها على قاعة أخرى لنقله فوراً — بدون فتح نافذة تعديل.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rooms-grid">
        {data.rooms.map((r, i) => (
          <RoomCard
            key={r.room_number}
            room={r}
            accent={ROOM_ACCENTS[i % ROOM_ACCENTS.length]}
            onDragStart={onDragStart}
            onDrop={onDrop}
            dragging={dragging}
            allowDrop={!!dragging}
            onCapacityChange={onCapacityChange}
          />
        ))}
      </div>

      {data.unassigned && (
        <Card
          className={`border-2 border-slate-200 border-dashed transition-all ${dragging && dragFromRoom !== 0 ? "ring-4 ring-slate-300 scale-[1.005]" : ""}`}
          data-testid="unassigned-card"
          onDragOver={(e) => { if (dragging) e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); onDropUnassigned(); }}
        >
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <HelpCircle className="w-5 h-5 text-slate-500" />
              <div className="font-black text-slate-700">معلمون بدون قاعة ({data.unassigned.teacher_count})</div>
              <div className="text-[11px] text-slate-500">— أفلت هنا لإزالة قاعة معلم، أو عيّن قاعة له بالسحب</div>
            </div>
            {data.unassigned.teacher_count === 0 ? (
              <div className="text-center py-4 text-slate-400 text-xs">لا يوجد معلمون بلا قاعة — ممتاز!</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {data.unassigned.teachers.map((t) => (
                  <div
                    key={t.id}
                    className={`flex items-center gap-2.5 bg-slate-50 border border-slate-200 rounded-xl p-2 cursor-move hover:shadow-md ${dragging === t.id ? "opacity-40" : ""}`}
                    draggable
                    onDragStart={(e) => { onDragStart(t.id, 0); e.dataTransfer.effectAllowed = "move"; }}
                    data-testid={`unassigned-teacher-${t.id}`}
                  >
                    <div className="w-9 h-9 rounded-xl overflow-hidden bg-gradient-to-br from-slate-500 to-slate-600 text-white flex items-center justify-center font-black text-xs">
                      {t.photo_url ? <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover" /> : (t.name || "?")[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-sm text-slate-800 truncate">{t.name}</div>
                      {t.specialty && <div className="text-[10px] text-slate-500 truncate">{t.specialty}</div>}
                    </div>
                    <div className="inline-flex items-center gap-1 text-[11px] font-black text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                      <Baby className="w-3 h-3" /> {t.assigned_children_count}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
